import axios from 'axios';
import { call, put, take, takeEvery } from 'redux-saga/effects';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import { PayloadAction } from '@reduxjs/toolkit';
import fileDownload from 'js-file-download';
import { TSwissPostOrdersRequest, TSwissPostOrderRequest } from 'redux/reducers/shipments/types';
import {
	TEuDdpOrderResponsePayload,
	TEuDdpOrderIdResponsePayload,
	TEuDdpOrderStatusesResponsePayload,
	TEuDdpRequestShipmentRequest,
	TEuDdpRequestShipmentResponsePayload,
	TEuDdpConfirmShipmentRequest,
	TEuDdpUpdateWeightRequest,
	TEuDdpUpdateDescriptionRequest,
	TEuDdpUpdateHs6CodeRequest,
	TEuDdpUpdateOrderFieldValueRequest,
	TEuDdpDownloadProformaInvoiceRequest,
	TEuDdpDownloadProformaInvoiceResponsePayload,
	TEuDdpUploadProformaInvoiceRequest,
	TEuDdpGetSignedProformaInvoiceRequest,
	TEuDdpGetSignedProformaInvoiceResponsePayload,
	TEuDdpDownloadLabelRequest,
	TEuDdpDownloadLabelResponsePayload,
	TEuDdpDeliveryOperatorsResponsePayload,
	TEuDdpS10Request,
	TEuDdpS10ResponsePayload,
} from './types';
import {
	getEuDdpOrdersRequest,
	getEuDdpOrdersSuccess,
	getEuDdpOrderRequest,
	getEuDdpOrderRequestWithoutReload,
	getEuDdpOrderSuccess,
	setEuDdpOrderError,
	getEuDdpOrderStatusesRequest,
	getEuDdpOrderStatusesSuccess,
	requestEuDdpShipmentRequest,
	requestEuDdpShipmentSuccess,
	setEuDdpRequestShipmentError,
	confirmEuDdpShipmentRequest,
	confirmEuDdpShipmentSuccess,
	setEuDdpConfirmShipmentError,
	updateEuDdpWeightRequest,
	updateEuDdpWeightSuccess,
	updateEuDdpWeightFailure,
	updateEuDdpDescriptionRequest,
	updateEuDdpDescriptionSuccess,
	updateEuDdpHs6CodeRequest,
	updateEuDdpHs6CodeSuccess,
	updateEuDdpOrderFieldValueRequest,
	updateEuDdpOrderFieldValueSuccess,
	downloadEuDdpProformaInvoiceRequest,
	downloadEuDdpProformaInvoiceSuccess,
	uploadEuDdpProformaInvoiceRequest,
	uploadEuDdpProformaInvoiceSuccess,
	getEuDdpSignedProformaInvoiceRequest,
	getEuDdpSignedProformaInvoiceSuccess,
	downloadEuDdpLabelRequest,
	downloadEuDdpLabelSuccess,
	getEuDdpDeliveryOperatorsRequest,
	getEuDdpDeliveryOperatorsSuccess,
	requestEuDdpS10Request,
	requestEuDdpS10Success,
	setEuDdpS10Error,
	euDdpInitState,
} from './reducer';
import { refreshTokenByTimeOutRequest, refreshTokenByTimeOutSuccess } from '../auth/reducer';

function* getEuDdpOrdersRequestWorker(action: PayloadAction<TSwissPostOrdersRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TEuDdpOrderResponsePayload = yield call(
			api.euDdp.getEuDdpOrders,
			action.payload,
		);
		yield put(getEuDdpOrdersSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		yield put(euDdpInitState());
	}
}

function* getEuDdpOrderRequestWorker(action: PayloadAction<TSwissPostOrderRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TEuDdpOrderIdResponsePayload = yield call(
			api.euDdp.getEuDdpOrder,
			action.payload,
		);
		yield put(getEuDdpOrderSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			if (error.response?.status === 404) {
				yield put(
					setEuDdpOrderError(error?.response?.data?.message ?? error.message ?? 'Order not found'),
				);
			} else {
				notificationContainer(
					error?.response?.data?.message ?? error.message ?? 'Request error',
					'error',
				);
				yield put(getEuDdpOrderSuccess(null));
			}
		}
	}
}

function* getEuDdpOrderStatusesRequestWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TEuDdpOrderStatusesResponsePayload = yield call(
			api.euDdp.getEuDdpOrderStatuses,
		);
		yield put(getEuDdpOrderStatusesSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
	}
}

function* requestEuDdpShipmentWorker(action: PayloadAction<TEuDdpRequestShipmentRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TEuDdpRequestShipmentResponsePayload = yield call(
			api.euDdp.requestEuDdpShipment,
			action.payload,
		);
		yield put(requestEuDdpShipmentSuccess(response));
		yield put(getEuDdpOrderRequest({ orderId: action.payload.orderId }));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			yield put(
				setEuDdpRequestShipmentError(
					error?.response?.data?.message ?? error.message ?? 'Request error',
				),
			);
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		} else {
			yield put(requestEuDdpShipmentSuccess(null));
		}
	}
}

function* confirmEuDdpShipmentWorker(action: PayloadAction<TEuDdpConfirmShipmentRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.euDdp.confirmEuDdpShipment, action.payload);
		yield put(confirmEuDdpShipmentSuccess());
		notificationContainer('Shipment has been confirmed', 'success');
		yield put(getEuDdpOrderRequestWithoutReload({ orderId: action.payload.orderId }));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			yield put(
				setEuDdpConfirmShipmentError(
					error?.response?.data?.message ?? error.message ?? 'Request error',
				),
			);
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		} else {
			yield put(confirmEuDdpShipmentSuccess());
		}
	}
}

function* updateEuDdpWeightWorker(action: PayloadAction<TEuDdpUpdateWeightRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.euDdp.updateEuDdpWeight, action.payload);
		yield put(updateEuDdpWeightSuccess());
		notificationContainer('Weight has been updated', 'success');
		yield put(getEuDdpOrderRequestWithoutReload({ orderId: action.payload.orderId }));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		yield put(updateEuDdpWeightFailure(action.payload.productId));
	}
}

function* updateEuDdpDescriptionWorker(action: PayloadAction<TEuDdpUpdateDescriptionRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.euDdp.updateEuDdpDescription, action.payload);
		yield put(updateEuDdpDescriptionSuccess());
		notificationContainer('Customs descriptions have been updated', 'success');
		yield put(getEuDdpOrderRequestWithoutReload({ orderId: action.payload.orderId }));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		yield put(updateEuDdpDescriptionSuccess());
	}
}

function* updateEuDdpHs6CodeWorker(action: PayloadAction<TEuDdpUpdateHs6CodeRequest>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.euDdp.updateEuDdpHs6Code, action.payload);
		yield put(updateEuDdpHs6CodeSuccess());
		notificationContainer('HS code has been updated', 'success');
		yield put(getEuDdpOrderRequestWithoutReload({ orderId: action.payload.orderId }));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		yield put(updateEuDdpHs6CodeSuccess());
	}
}

function* updateEuDdpOrderFieldValueWorker(
	action: PayloadAction<TEuDdpUpdateOrderFieldValueRequest>,
) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.euDdp.updateEuDdpOrderFieldValue, action.payload);
		yield put(updateEuDdpOrderFieldValueSuccess());
		notificationContainer('Order fields have been updated', 'success');
		yield put(getEuDdpOrderRequestWithoutReload({ orderId: action.payload.orderId }));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		yield put(updateEuDdpOrderFieldValueSuccess());
	}
}

function* downloadEuDdpProformaInvoiceWorker(
	action: PayloadAction<TEuDdpDownloadProformaInvoiceRequest>,
): Generator<any, void, any> {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TEuDdpDownloadProformaInvoiceResponsePayload = yield call(
			api.euDdp.downloadEuDdpProformaInvoice,
			action.payload,
		);
		yield put(downloadEuDdpProformaInvoiceSuccess());
		if (response) {
			yield fileDownload(response, `eas_eu_ddp_proforma__${action.payload.externalOrderId}.pdf`);
		}
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const responseData = yield error?.response?.data.text().then((data: string) => data);
			notificationContainer(JSON.parse(responseData)?.message ?? 'File download error', 'error');
		}
		yield put(downloadEuDdpProformaInvoiceSuccess());
	}
}

function* uploadEuDdpProformaInvoiceWorker(
	action: PayloadAction<TEuDdpUploadProformaInvoiceRequest>,
) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.euDdp.uploadEuDdpProformaInvoice, action.payload);
		yield put(uploadEuDdpProformaInvoiceSuccess());
		notificationContainer('Proforma has been uploaded', 'success');
		yield put(getEuDdpOrderRequestWithoutReload({ orderId: action.payload.orderId }));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		yield put(uploadEuDdpProformaInvoiceSuccess());
	}
}

function* getEuDdpSignedProformaInvoiceWorker(
	action: PayloadAction<TEuDdpGetSignedProformaInvoiceRequest>,
): Generator<any, void, any> {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TEuDdpGetSignedProformaInvoiceResponsePayload = yield call(
			api.euDdp.getEuDdpSignedProformaInvoice,
			action.payload,
		);
		yield put(getEuDdpSignedProformaInvoiceSuccess());
		if (response) {
			yield fileDownload(
				response,
				`eas_eu_ddp_signed_proforma__${action.payload.externalOrderId}.pdf`,
			);
		}
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const responseData = yield error?.response?.data.text().then((data: string) => data);
			notificationContainer(JSON.parse(responseData)?.message ?? 'File download error', 'error');
		}
		yield put(getEuDdpSignedProformaInvoiceSuccess());
	}
}

function* downloadEuDdpLabelWorker(
	action: PayloadAction<TEuDdpDownloadLabelRequest>,
): Generator<any, void, any> {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TEuDdpDownloadLabelResponsePayload = yield call(
			api.euDdp.downloadEuDdpLabel,
			action.payload,
		);
		yield put(downloadEuDdpLabelSuccess());
		if (response) {
			yield fileDownload(response, `eas_eu_ddp_label__${action.payload.orderId}.pdf`);
		}
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const responseData = yield error?.response?.data.text().then((data: string) => data);
			notificationContainer(JSON.parse(responseData)?.message ?? 'File download error', 'error');
		}
		yield put(downloadEuDdpLabelSuccess());
	}
}

function* getEuDdpDeliveryOperatorsRequestWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TEuDdpDeliveryOperatorsResponsePayload = yield call(
			api.euDdp.getEuDdpDeliveryOperators,
		);
		yield put(getEuDdpDeliveryOperatorsSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error))
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
	}
}

function* requestEuDdpS10Worker(action: PayloadAction<TEuDdpS10Request>) {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TEuDdpS10ResponsePayload = yield call(
			api.euDdp.requestEuDdpS10,
			action.payload,
		);
		yield put(requestEuDdpS10Success(response));
		yield put(getEuDdpOrderRequestWithoutReload({ orderId: action.payload.orderId }));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			yield put(
				setEuDdpS10Error(error?.response?.data?.message ?? error.message ?? 'Request error'),
			);
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		} else {
			yield put(requestEuDdpS10Success(null));
		}
	}
}

export function* euDdpSaga() {
	yield takeEvery(getEuDdpOrdersRequest.type, getEuDdpOrdersRequestWorker);
	yield takeEvery(getEuDdpOrderRequest.type, getEuDdpOrderRequestWorker);
	yield takeEvery(getEuDdpOrderRequestWithoutReload.type, getEuDdpOrderRequestWorker);
	yield takeEvery(getEuDdpOrderStatusesRequest.type, getEuDdpOrderStatusesRequestWorker);
	yield takeEvery(requestEuDdpShipmentRequest.type, requestEuDdpShipmentWorker);
	yield takeEvery(confirmEuDdpShipmentRequest.type, confirmEuDdpShipmentWorker);
	yield takeEvery(updateEuDdpWeightRequest.type, updateEuDdpWeightWorker);
	yield takeEvery(updateEuDdpDescriptionRequest.type, updateEuDdpDescriptionWorker);
	yield takeEvery(updateEuDdpHs6CodeRequest.type, updateEuDdpHs6CodeWorker);
	yield takeEvery(updateEuDdpOrderFieldValueRequest.type, updateEuDdpOrderFieldValueWorker);
	yield takeEvery(downloadEuDdpProformaInvoiceRequest.type, downloadEuDdpProformaInvoiceWorker);
	yield takeEvery(uploadEuDdpProformaInvoiceRequest.type, uploadEuDdpProformaInvoiceWorker);
	yield takeEvery(getEuDdpSignedProformaInvoiceRequest.type, getEuDdpSignedProformaInvoiceWorker);
	yield takeEvery(downloadEuDdpLabelRequest.type, downloadEuDdpLabelWorker);
	yield takeEvery(getEuDdpDeliveryOperatorsRequest.type, getEuDdpDeliveryOperatorsRequestWorker);
	yield takeEvery(requestEuDdpS10Request.type, requestEuDdpS10Worker);
}
